<template>
  <tr
    class="bg-gray-100 text-sm text-right child:py-5 cursor-pointer hover:bg-gray-200/80 transition-colors"
    @click.stop="open = !open"
  >
    <td class="pl-8 text-left">
      <div class="flex items-center space-x-1">
        <button
          type="button"
          class="btn-icon btn-icon-default"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
            :class="[{ 'rotate-90': open }]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <span>{{ group.label }}</span>
      </div>
    </td>

    <td class="pr-3">
      {{ $tcc(totals.budget) }}
    </td>

    <td class="bg-green-100/30">
      <span v-if="showSubcontractSummary">{{ $tcc(totals.subcontractClaim) }}</span>
    </td>

    <td
      v-if="claim.capture_claim_amount"
      class="bg-green-100/30"
    >
      {{ $tcc(totals.currentClaim) }}
    </td>

    <td class="bg-green-100/30">
      <span v-if="showSubcontractSummary">{{ $tcc(totals.subcontractCertified) }}</span>
    </td>

    <td class="bg-green-100/30">
      {{ $tcc(totals.currentCertified) }}
    </td>

    <td
      v-if="claim.capture_claim_amount"
      class="bg-green-100/30"
      :class="{ 'text-red-600': totals.variance !== 0 }"
    >
      {{ $tcc(totals.variance) }}
    </td>

    <td class="bg-green-100/30">
      {{ $tcc(totals.previous) }}
    </td>

    <td class="bg-green-100/30 pr-3">
      {{ $tcc(totals.cumulative) }}
    </td>

    <td
      v-if="claim.capture_claim_amount"
      class="bg-blue-100/30"
    >
      <span v-if="showPercentages">
        {{ totals.budget > 0 ? $tp((totals.currentClaim / totals.budget) * 100) : '' }}
      </span>
    </td>

    <td
      v-if="!claim.capture_claim_amount || showPercentages"
      class="bg-blue-100/30"
    >
      <span v-if="showPercentages">
        {{ totals.budget > 0 ? $tp((totals.currentCertified / totals.budget) * 100) : '' }}
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/30"
    >
      <span v-if="showPercentages">
        {{ totals.budget > 0 ? $tp((totals.previous / totals.budget) * 100) : '' }}
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/30 pr-3"
    >
      <span v-if="showPercentages">
        {{ totals.budget > 0 ? $tp((totals.cumulative / totals.budget) * 100) : '' }}
      </span>
    </td>

    <td class="pr-3">
      {{ $tcc(totals.budget - totals.cumulative) }}
    </td>
  </tr>

  <template v-if="open">
    <VariationOrderCostRow
      v-for="variationOrder in variationOrders"
      :key="variationOrder.id"
      :claim="claim"
      :variation-order="variationOrder"
      :show-percentages="showPercentages"
      :show-subcontract-summary="showSubcontractSummary"
      :variation-order-cost-ids-for-claim-period="variationOrderCostIdsForClaimPeriod"
      :depth="1"
    />
  </template>
</template>

<script setup lang="ts">
import Claim from '@/models/Claim';
import VariationOrder from '@/models/VariationOrder';
import { computed, ref } from 'vue';
import VariationOrderCostRow from './VariationOrderCostRow.vue';

export type VariationOrderCategoryGroup = {
  key: string;
  label: string;
};

const props = defineProps<{
  group: VariationOrderCategoryGroup;
  variationOrders: VariationOrder[];
  claim: Claim;
  showPercentages?: boolean;
  showSubcontractSummary?: boolean;
  variationOrderCostIdsForClaimPeriod: Id[];
}>();

const open = ref(false);

const totals = computed(() => {
  const result = {
    budget: 0,
    currentClaim: 0,
    currentCertified: 0,
    previous: 0,
    cumulative: 0,
    variance: 0,
    subcontractClaim: 0,
    subcontractCertified: 0,
  };

  props.variationOrders.forEach((variationOrder) => {
    result.budget += variationOrder.totalBudget;
    result.previous += variationOrder.previous(props.claim);

    const variationOrderCost = variationOrder.costs.find((cost) => {
      return cost.claim_id === props.claim.id;
    });

    if(variationOrderCost) {
      result.currentClaim += variationOrderCost.current_claim;
      result.currentCertified += variationOrderCost.current_certified;
      result.variance += variationOrderCost.current_claim - variationOrderCost.current_certified;
    }

    result.subcontractClaim += variationOrder.subcontractSummaryClaim(props.variationOrderCostIdsForClaimPeriod);

    result.subcontractCertified += variationOrder.subcontractSummaryCertified(
      props.variationOrderCostIdsForClaimPeriod,
    );
  });

  result.cumulative = result.previous + result.currentCertified;

  return result;
});

defineOptions({
  inheritAttrs: false,
});
</script>
